import { BioProps, TagQuestions } from "@hubcapp/views/MyBusinessProfile/types";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Calendar } from "react-feather";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { Skeleton } from "@material-ui/lab";
import { questions } from "./questions";
import Button from "@ds-developer/button";
import React from "react";
import TagSelectionMultiSelect from "@hubcapp/views/MyBusinessProfile/ProfileDetails/components/Bio/components/TagSelectionMultiSelect";

const useStyles = makeStyles(theme => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
  },
  sectionTitle: {
    margin: theme.spacing(0, 0, 2, 0),
  },
  title: {
    padding: theme.spacing(0),
    marginBottom: theme.spacing(2),
  },
  subTitle: {
    fontWeight: 300,
    fontSize: "15px",
    marginBottom: theme.spacing(2),
  },
  autocomplete: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      minWidth: "500px",
    },
  },
  autocompleteInput: {},
  formField: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      minWidth: "500px",
    },
  },
  loading: {
    paddingTop: theme.spacing(2),
  },
}));

const Bio: React.FC<BioProps> = ({
  values,
  onBadgeDateChange,
  onQuestionChange,
  onGenerateBioClick,
  isGeneratingBio,
  isBioGenerationDisabled,
  badgeType,
  errors,
  loading,
}) => {
  const classes = useStyles();

  const handleBadgeDateChange = (date: MaterialUiPickersDate) => {
    onBadgeDateChange(date?.toDateString() || "");
  };

  return (
    <>
      <Card>
        <CardContent>
          <Grid container direction="column" spacing={2}>
            <Grid item sm={12} xs={12}>
              <Typography
                variant="h2"
                color="textSecondary"
                className={classes.title}
              >
                Bio
              </Typography>
              <Typography
                variant="h3"
                color="textPrimary"
                className={classes.subTitle}
              >
                Answer the following questions to generate your instructor bio
                for your public business profile.
              </Typography>
            </Grid>
          </Grid>
          {loading ? (
            <Grid className={classes.loading}>
              {[...Array(3)].map((_, idx) => (
                <Box key={idx} mb={2}>
                  <Skeleton key={idx} width="100%" height="35px" />
                </Box>
              ))}
            </Grid>
          ) : (
            <>
              <Grid container direction="column" spacing={2}>
                <Grid item lg={6} xs={12} className={classes.formField}>
                  <KeyboardDatePicker
                    id={`profile-details-${badgeType.toLowerCase()}-badge-date`}
                    data-test={`profile-details-${badgeType.toLowerCase()}-badge-date`}
                    fullWidth
                    label={`What date was your ${badgeType.toLowerCase()} badge issued?`}
                    minDateMessage=""
                    maxDateMessage=""
                    autoOk={true}
                    onChange={handleBadgeDateChange}
                    value={values?.badgeDate || null}
                    placeholder="dd/mm/yyyy"
                    variant="inline"
                    format="dd/MM/yyyy"
                    inputVariant="outlined"
                    error={Boolean(errors?.badgeDate)}
                    disableToolbar
                    keyboardIcon={<Calendar />}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={isGeneratingBio}
                  />
                </Grid>
                {questions.map(({ key, label, tags }) => (
                  <Grid
                    item
                    lg={6}
                    xs={12}
                    className={classes.formField}
                    key={key}
                  >
                    <TagSelectionMultiSelect
                      tagOptions={tags}
                      onTagChange={newValues => {
                        if (newValues.length <= 3) {
                          onQuestionChange(key, newValues);
                        }
                      }}
                      values={values?.[key as keyof TagQuestions] || []}
                      label={label}
                      maxSelection={3}
                      disabled={isGeneratingBio}
                    />
                  </Grid>
                ))}
                <Grid item lg={6} xs={12} className={classes.formField}>
                  <Button
                    data-test="generate-bio-button"
                    variant="contained"
                    color="primary"
                    onClick={onGenerateBioClick}
                    disabled={isGeneratingBio || isBioGenerationDisabled}
                    loading={isGeneratingBio}
                  >
                    Generate Bio
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>
      <br />
      {values?.bio?.split("\\n").map((paragraph, index) => (
        <Grid key={index} item xs={12}>
          <Typography>{paragraph}</Typography>
        </Grid>
      ))}
    </>
  );
};

export default Bio;
